import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import ReactDOM from 'react-dom/client';
import { useIntl } from 'react-intl';

import { GlobalError } from './components/global/Error';
import { FullLoader } from './components/global/Loader';
import { PostHogProvider } from './components/providers/PostHogProvider';
import { TranslationsProvider } from './components/providers/TranslationsProvider';
import { Toaster } from './components/ui/toaster';
import { ViteEnv } from './env';
import { useToast } from './hooks/use-toast';
import { parseMutationKey } from './lib/queries/utils';
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: async (
      _data,
      _variables,
      _context,
      { options: { mutationKey } },
    ) => {
      if (mutationKey === undefined) {
        return;
      }

      const { queryKey, invalidate } = parseMutationKey(mutationKey);

      if (invalidate) {
        await queryClient.invalidateQueries({
          queryKey,
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1, // By default, queries will be considered stale after 1 minute (Overridable per-query)
      gcTime: 1000 * 60 * 5, // By default, queries will be garbage collected after 5 minutes (Overridable per-query)
    },
  },
});

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    queryClient: undefined!,
    title: undefined!,
    formatMessage: undefined!,
    toast: undefined!,
  },
  defaultErrorComponent: GlobalError,
  defaultPendingComponent: FullLoader,
});

Sentry.init({
  dsn: ViteEnv.VITE_SENTRY_DSN,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  tracesSampleRate: 1.0,
  enabled: import.meta.env.PROD,
});

// Register things for type-safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('app');

const InnerRouter = () => {
  const { formatMessage } = useIntl();
  const { toast } = useToast();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        title: undefined,
        formatMessage,
        toast,
      }}
    />
  );
};

if (rootElement !== null && rootElement.innerHTML === '') {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <PostHogProvider>
      <QueryClientProvider client={queryClient}>
        <TranslationsProvider>
          <InnerRouter />
          <Toaster />
        </TranslationsProvider>
      </QueryClientProvider>
    </PostHogProvider>,
  );
}
